/**
 * Feature tracking: rotation maths, the union-find forest, and completion detection. [P1-features]
 *
 * A "feature" in play (one city, one road, one farm) is a set of per-tile features that have been
 * joined by adjacency. We track that with a union-find forest over nodes keyed `"x,y#featureIndex"`.
 *
 * Union-find is the right structure because the only thing that ever happens to features is *merging*:
 * placing one tile can join two previously separate cities into one, and both sets of meeples must
 * come along. Anything less than a disjoint-set structure gets that case wrong.
 *
 * The forest is persistent (copy-on-write). Each action produces a new forest rather than mutating the
 * old one, so `applyAction` stays pure and a caller can hold onto a previous state safely. At ~300
 * nodes for a full game the copy is trivial.
 */
import { OPPOSITE, SIDES, SIDE_OFFSET, SUBEDGE_TERRAIN, posKey } from './types.js';
// ── Rotation ──────────────────────────────────────────────────────────────────────────────────────
/**
 * The terrain presented on `side` by a tile rotated `rotation` quarter-turns clockwise.
 * Rotating the tile clockwise means the side now facing north was previously facing west, etc.
 */
export function edgeAt(def, rotation, side) {
    const original = (((side - rotation) % 4) + 4) % 4;
    return def.edges[original];
}
/** Where an unrotated sub-edge index ends up after rotation. */
export const rotateSubEdge = (subEdge, rotation) => (subEdge + rotation * 3) % 12;
/** The inverse: which unrotated sub-edge currently sits at `subEdge`. */
export const unrotateSubEdge = (subEdge, rotation) => ((subEdge - rotation * 3) % 12 + 12) % 12;
/** The three sub-edge indices of a side, in clockwise order. */
export const subEdgesOfSide = (side) => [
    side * 3,
    side * 3 + 1,
    side * 3 + 2,
];
/**
 * Two tiles meeting across a boundary pair their sub-edges in REVERSE order: my north-west touches my
 * neighbour's south-east. Getting this backwards silently mis-joins farms on road tiles — and only on
 * road tiles, which makes it a nightmare to spot. Hence its own named function.
 */
export function facingSubEdge(subEdge) {
    const side = Math.floor(subEdge / 3);
    const offsetInSide = subEdge % 3;
    return OPPOSITE[side] * 3 + (2 - offsetInSide);
}
/** The terrain of a single sub-edge on a rotated tile. */
export function subEdgeTerrain(def, rotation, subEdge) {
    const side = Math.floor(subEdge / 3);
    const offsetInSide = subEdge % 3;
    return SUBEDGE_TERRAIN[edgeAt(def, rotation, side)][offsetInSide];
}
/** Which feature of the tile owns a given (rotated) sub-edge, or -1 if none does. */
export function featureAtSubEdge(def, rotation, subEdge) {
    const unrotated = unrotateSubEdge(subEdge, rotation);
    for (let i = 0; i < def.features.length; i++) {
        const f = def.features[i];
        if (f && f.subEdges.includes(unrotated))
            return i;
    }
    return -1;
}
export const nodeId = (pos, featureIndex) => `${pos.x},${pos.y}#${featureIndex}`;
export function parseNodeId(id) {
    const hash = id.indexOf('#');
    const comma = id.indexOf(',');
    return {
        pos: { x: Number(id.slice(0, comma)), y: Number(id.slice(comma + 1, hash)) },
        featureIndex: Number(id.slice(hash + 1)),
    };
}
export const createForest = () => ({ parent: new Map(), rank: new Map() });
/** Non-mutating find. No path compression — trees stay shallow under union-by-rank at this scale. */
export function find(forest, id) {
    let current = id;
    for (;;) {
        const parent = forest.parent.get(current);
        if (parent === undefined || parent === current)
            return current;
        current = parent;
    }
}
export function addNode(forest, id) {
    if (forest.parent.has(id))
        return forest;
    const parent = new Map(forest.parent);
    const rank = new Map(forest.rank);
    parent.set(id, id);
    rank.set(id, 0);
    return { parent, rank };
}
export function union(forest, a, b) {
    const rootA = find(forest, a);
    const rootB = find(forest, b);
    if (rootA === rootB)
        return forest;
    const parent = new Map(forest.parent);
    const rank = new Map(forest.rank);
    const rankA = rank.get(rootA) ?? 0;
    const rankB = rank.get(rootB) ?? 0;
    if (rankA < rankB) {
        parent.set(rootA, rootB);
    }
    else if (rankA > rankB) {
        parent.set(rootB, rootA);
    }
    else {
        parent.set(rootB, rootA);
        rank.set(rootA, rankA + 1);
    }
    return { parent, rank };
}
/** Every node currently sharing a root with `id`. */
export function membersOf(forest, id) {
    const root = find(forest, id);
    const out = [];
    for (const node of forest.parent.keys()) {
        if (find(forest, node) === root)
            out.push(node);
    }
    return out;
}
/** Every distinct feature root in the forest. */
export function roots(forest) {
    const seen = new Set();
    for (const node of forest.parent.keys())
        seen.add(find(forest, node));
    return [...seen];
}
/**
 * Describe the feature containing `id`.
 *
 * Completion is "no open edges": walk every sub-edge the feature owns and check the neighbouring cell
 * is filled. A city with one wall opening onto empty space is unfinished, however large it is.
 */
export function describeFeature(forest, board, tiles, id) {
    const nodes = membersOf(forest, id);
    if (nodes.length === 0)
        return null;
    const first = parseNodeId(nodes[0]);
    const firstTile = board.get(posKey(first.pos));
    if (!firstTile)
        return null;
    const firstDef = tiles.get(firstTile.defId);
    if (!firstDef)
        return null;
    const kind = firstDef.features[first.featureIndex]?.kind;
    if (!kind)
        return null;
    const tilePositions = new Set();
    let pennants = 0;
    let complete = kind !== 'field';
    for (const node of nodes) {
        const { pos, featureIndex } = parseNodeId(node);
        const placed = board.get(posKey(pos));
        if (!placed)
            continue;
        const def = tiles.get(placed.defId);
        if (!def)
            continue;
        const feature = def.features[featureIndex];
        if (!feature)
            continue;
        tilePositions.add(posKey(pos));
        if (feature.pennant)
            pennants++;
        if (kind === 'cloister') {
            // A cloister completes when all eight surrounding cells are filled.
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    if (dx === 0 && dy === 0)
                        continue;
                    if (!board.has(posKey({ x: pos.x + dx, y: pos.y + dy })))
                        complete = false;
                }
            }
        }
        else if (kind !== 'field') {
            // Roads and cities complete when every sub-edge they own faces a placed tile.
            for (const unrotated of feature.subEdges) {
                const rotated = rotateSubEdge(unrotated, placed.rotation);
                const side = Math.floor(rotated / 3);
                const offset = SIDE_OFFSET[side];
                if (!board.has(posKey({ x: pos.x + offset.x, y: pos.y + offset.y }))) {
                    complete = false;
                }
            }
        }
    }
    return {
        root: find(forest, id),
        kind,
        nodes,
        tiles: [...tilePositions],
        pennants,
        complete,
    };
}
// ── Joining a newly placed tile into the forest ───────────────────────────────────────────────────
/**
 * Register a newly placed tile's features and union them with whatever they now touch.
 *
 * This is where a placement can silently merge two large cities, or graft a farm onto three others.
 * Everything downstream — occupancy checks, completion, scoring — reads the result of this function,
 * so it runs BEFORE the "is this feature already occupied?" test ([P1-meeple]).
 */
export function integrateTile(forest, board, tiles, placed) {
    const def = tiles.get(placed.defId);
    if (!def)
        return forest;
    let next = forest;
    for (let i = 0; i < def.features.length; i++) {
        next = addNode(next, nodeId(placed.pos, i));
    }
    for (const side of SIDES) {
        const offset = SIDE_OFFSET[side];
        const neighbourPos = { x: placed.pos.x + offset.x, y: placed.pos.y + offset.y };
        const neighbour = board.get(posKey(neighbourPos));
        if (!neighbour)
            continue;
        const neighbourDef = tiles.get(neighbour.defId);
        if (!neighbourDef)
            continue;
        for (const mySubEdge of subEdgesOfSide(side)) {
            const theirSubEdge = facingSubEdge(mySubEdge);
            const myFeature = featureAtSubEdge(def, placed.rotation, mySubEdge);
            const theirFeature = featureAtSubEdge(neighbourDef, neighbour.rotation, theirSubEdge);
            if (myFeature < 0 || theirFeature < 0)
                continue;
            // Only join like with like. Placement legality already guarantees the side terrains match, but
            // a road side pairs a road sub-edge with a road sub-edge AND two field pairs — so we still check.
            const mine = subEdgeTerrain(def, placed.rotation, mySubEdge);
            const theirs = subEdgeTerrain(neighbourDef, neighbour.rotation, theirSubEdge);
            if (mine !== theirs)
                continue;
            next = union(next, nodeId(placed.pos, myFeature), nodeId(neighbourPos, theirFeature));
        }
    }
    return next;
}
//# sourceMappingURL=features.js.map